import React, { useEffect, useState } from 'react';
import { Avatar, Badge, Box, Button, Dialog, DialogActions, IconButton } from '@mui/material';
import { CloudUpload, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import Bluebird from 'bluebird';
import { App } from '@capacitor/app';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import snackbarHelper from '../helpers/snackbarHelper';
import { Colors } from '../assets/theme/Colors';
import { generateS3Url, s3FileUpload } from '../api/upload';
import ProfilePictureCrop from './ProfilePictureCrop';
import ButtonWithLoading from './ButtonWithLoading';
import PhotoActionSheet from './Native/PhotoActionSheet';

const getCroppedImage = (crop, selectedImage, fileName) => {
  const canvas = document.createElement('canvas');
  const scaleX = selectedImage.naturalWidth / selectedImage.width;
  const scaleY = selectedImage.naturalHeight / selectedImage.height;
  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    selectedImage,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        blob.name = fileName;
        resolve(blob);
      },
      'image/jpeg',
      0.5 // quality
    );
  });
};

// Round picture with edit/remove badges, crop dialog and S3 upload (profile-pictures bucket), shared by the rescuer
// profile picture (UserForm) and the food donor / receiving agency logo (LogoUploadField).
// It only uploads the file - `onChange(url | null)` decides what happens with the URL: UserForm saves it right away,
// LogoUploadField keeps it in the form until Update. If `onChange` returns a promise, the picture stays in loading state
// until it settles.
const PictureUpload = ({
  value,
  onChange,
  src,
  alt,
  children,
  editable = true,
  fileName = 'picture.jpg',
  label = 'picture',
}) => {
  const [loading, setLoading] = useState(false);
  const [showImageCropDialog, setShowImageCropDialog] = useState(false);
  const [showPhotoActionSheet, setShowPhotoActionSheet] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState(null);

  // Needed for Android - it can suspend the app to save memory when opening the camera, this listener restores the
  // result from the camera. Only this listener is removed on unmount (removeAllListeners would also drop the deep link
  // and Auth0 `appUrlOpen` listeners).
  useEffect(() => {
    if (!editable || !Capacitor.isNativePlatform()) {
      return undefined;
    }

    let listener = null;
    let unmounted = false;

    App.addListener('appRestoredResult', (data) => {
      if (data.pluginId === 'Camera' && data.success) {
        setSelectedImageUrl(data.data.dataUrl);
        setShowImageCropDialog(true);
      }
    }).then((handle) => {
      if (unmounted) {
        handle.remove();
      } else {
        listener = handle;
      }
    });

    return () => {
      unmounted = true;

      if (listener) {
        listener.remove();
      }
    };
  }, [editable]);

  const handleDialogClose = () => {
    setShowImageCropDialog(false);
    setSelectedImageUrl('');
    setSelectedImage(null);
    setCrop(null);
  };

  const setCroppedPicture = (newCrop, image) => {
    setCrop(newCrop);
    setSelectedImage(image);
  };

  const handlePictureUpload = async () => {
    setLoading(true);

    const croppedImage = await getCroppedImage(crop, selectedImage, fileName);

    return Bluebird.try(() => generateS3Url(croppedImage.name, 'profile-pictures'))
      .then((res) => res.json())
      .then((res) => res.data)
      .then((signedData) =>
        Bluebird.try(() =>
          s3FileUpload({
            url: signedData.url,
            file: croppedImage,
          })
        ).then(() => {
          const matches = signedData.url.match(/(.*)\?/);

          if (matches && Array.isArray(matches) && matches[1]) {
            return onChange(matches[1]);
          }

          return snackbarHelper.error(`Couldn't upload ${label}.`);
        })
      )
      .catch((err) => snackbarHelper.error(err.message))
      .finally(() => {
        setLoading(false);
        handleDialogClose();
      });
  };

  const handlePictureRemove = () => {
    setLoading(true);

    return Bluebird.try(() => onChange(null))
      .catch((err) => snackbarHelper.error(err.message))
      .finally(() => setLoading(false));
  };

  // Capacitor functionality
  const readFilesNative = async (action) => {
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: action === 'camera' ? CameraSource.Camera : CameraSource.Photos,
        saveToGallery: action === 'camera',
        quality: 80,
        format: 'jpeg',
      });

      setSelectedImageUrl(photo.dataUrl);
      setShowImageCropDialog(true);
    } catch (e) {
      // the user closed the camera / gallery without picking a photo
    }
  };

  // Browser functionality
  const readFiles = (event) => {
    event.preventDefault();
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    setSelectedImageUrl(URL.createObjectURL(file));
    setShowImageCropDialog(true);
  };

  return (
    <>
      <Box display="flex" justifyContent="center">
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            editable && (
              <Box
                sx={{
                  backgroundColor: 'primary.main',
                  border: `2px solid ${Colors.white}`,
                  borderRadius: '50%',
                }}
              >
                {Capacitor.isNativePlatform() ? (
                  <IconButton
                    size="small"
                    sx={{ color: Colors.white }}
                    onClick={() => setShowPhotoActionSheet(true)}
                    aria-label={`Upload ${label}`}
                  >
                    <EditIcon />
                  </IconButton>
                ) : (
                  <IconButton
                    component="label"
                    size="small"
                    sx={{ color: Colors.white }}
                    aria-label={`Upload ${label}`}
                  >
                    <input
                      accept="image/*"
                      name="picture"
                      onClick={(event) => (event.target.value = '')}
                      type="file"
                      onChange={readFiles}
                      hidden
                    />
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            )
          }
        >
          <Avatar
            alt={alt}
            src={(src !== undefined ? src : value) || undefined}
            sx={{ height: '160px', width: '160px', fontSize: '4rem', border: '4px solid white' }}
          >
            {children}
          </Avatar>
          {editable && value && (
            <Box
              sx={{
                position: 'absolute',
                left: '14%',
                bottom: '14%',
                transform: 'scale(1) translate(50%, 50%)',
                transformOrigin: '100% 100%',
              }}
            >
              <Badge
                overlap="circular"
                badgeContent={
                  <Box
                    sx={{
                      backgroundColor: 'error.main',
                      border: `2px solid ${Colors.white}`,
                      borderRadius: '50%',
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{ color: Colors.white }}
                      disabled={loading}
                      onClick={handlePictureRemove}
                      aria-label={`Remove ${label}`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
              />
            </Box>
          )}
        </Badge>
      </Box>

      <Dialog maxWidth="lg" open={showImageCropDialog} onClose={handleDialogClose}>
        <ProfilePictureCrop picture={selectedImageUrl} setCroppedPicture={setCroppedPicture} />
        <DialogActions>
          <Button disabled={loading} variant="outlined" onClick={handleDialogClose}>
            Cancel
          </Button>
          <ButtonWithLoading
            isLoading={loading}
            disabled={!crop || !selectedImage || loading}
            variant="contained"
            color="primary"
            endIcon={<CloudUpload />}
            onClick={handlePictureUpload}
          >
            Upload
          </ButtonWithLoading>
        </DialogActions>
      </Dialog>

      <PhotoActionSheet
        open={showPhotoActionSheet}
        onClose={() => setShowPhotoActionSheet(false)}
        onActionClick={readFilesNative}
      />
    </>
  );
};

export default PictureUpload;
